import { useState, useEffect, useRef } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Chip,
  CircularProgress,
} from '@mui/material';
import { QrCodeScanner, PersonAdd } from '@mui/icons-material';
import { collection, query, where, getDocs, addDoc, increment, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTenant } from '../providers/TenantProvider';

interface MemberInfo {
  id: string;
  contact: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  barcodeId: string;
  status: string;
  planId?: string;
  attendanceCount: number;
}

export const CheckInPage = () => {
  const { tenantId } = useTenant();
  const [barcodeInput, setBarcodeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [member, setMember] = useState<MemberInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the input field when the page loads
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setMember(null);
        setBarcodeInput('');
        inputRef.current?.focus();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleBarcodeSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!barcodeInput.trim()) {
      setError('Please enter or scan a barcode');
      return;
    }

    if (!tenantId) {
      setError('No tenant context available');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setMember(null);

    try {
      // Look up member by barcode
      const membersQuery = query(
        collection(db, 'members'),
        where('tenantId', '==', tenantId),
        where('barcodeId', '==', barcodeInput.trim())
      );

      const membersSnapshot = await getDocs(membersQuery);

      if (membersSnapshot.empty) {
        setError('Member not found. Please check the barcode and try again.');
        setBarcodeInput('');
        return;
      }

      const memberDoc = membersSnapshot.docs[0];
      const memberData = {
        id: memberDoc.id,
        ...memberDoc.data(),
      } as MemberInfo;

      setMember(memberData);

      // Check if member is active
      if (memberData.status !== 'active') {
        setError(`Member status is "${memberData.status}". Please contact staff.`);
        return;
      }

      // Create check-in record
      await addDoc(collection(db, 'checkins'), {
        tenantId,
        memberId: memberDoc.id,
        timestamp: new Date(),
        method: 'barcode',
        createdAt: new Date(),
      });

      // Increment attendance count
      await updateDoc(doc(db, 'members', memberDoc.id), {
        attendanceCount: increment(1),
        updatedAt: new Date(),
      });

      setSuccess(`Welcome, ${memberData.contact.firstName} ${memberData.contact.lastName}!`);
      
      // Play success sound (optional)
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUKbj8LdkHQU5k9jx0H4sBSh+zPDajz4KFWC26+usVxQKR6Hh8cBuIQUsgs/y24o3CBxrvfDlnU4NEFKo4/C5ZR0GOZXa8NJ/KwUpfs/w3I5AChViuOzrsVsVDUml4vHCcSMGLYPS8t2KNwgdbL/w6J9ODRBTqOTwumYeBjqV2vHTgCwGKn/P8N+PQQoWY7js7bJcFw5Lp+Pxw3ImBi+E1PLei0AIHm3A8emeUQ0RVKjl8bxnHgc6ltvy1YEtBiuA0PHgjkIKF2S66+yzXRgPTKnl8sR0JwYxhdTz4ItACB9uwfHrnlINElWp5fK9aB8HOphc89aGLgYsgdHw5I5DChhlvOzttF4ZEE2q5vLFdSgHM4XW8+KKQQ0gAA==');
      audio.play().catch(() => {
        // Ignore audio errors
      });

    } catch (err: any) {
      console.error('Error checking in member:', err);
      setError(err.message || 'Failed to check in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <QrCodeScanner sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" gutterBottom>
            Member Check-In
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Scan member barcode or enter barcode manually
          </Typography>
        </Box>

        <Paper sx={{ p: 4, mb: 3 }}>
          <Box component="form" onSubmit={handleBarcodeSubmit}>
            <TextField
              inputRef={inputRef}
              fullWidth
              label="Barcode ID"
              placeholder="Scan or enter barcode (e.g., GYM-1234567890-ABC123)"
              value={barcodeInput}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setBarcodeInput(e.target.value)}
              disabled={loading}
              autoComplete="off"
              sx={{ mb: 3 }}
              InputProps={{
                sx: { fontSize: '1.25rem' },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading || !barcodeInput.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <PersonAdd />}
            >
              {loading ? 'Checking In...' : 'Check In'}
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && member && (
          <Card
            sx={{
              bgcolor: 'success.light',
              color: 'success.contrastText',
              mb: 3,
              border: 3,
              borderColor: 'success.main',
            }}
          >
            <CardContent>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                ✓ {success}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">
                  {member.contact.firstName} {member.contact.lastName}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Email: {member.contact.email}
                </Typography>
                {member.contact.phone && (
                  <Typography variant="body2">
                    Phone: {member.contact.phone}
                  </Typography>
                )}
                <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label={`Status: ${member.status}`}
                    color={member.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={`Total Check-ins: ${member.attendanceCount + 1}`}
                    color="info"
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {member && !success && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Member Information
              </Typography>
              <Typography variant="body1">
                {member.contact.firstName} {member.contact.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {member.contact.email}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`Status: ${member.status}`}
                  color={member.status === 'active' ? 'success' : 'warning'}
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default CheckInPage;
